import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import adminApi from '@/lib/adminApi';

const navItems = [
  { label: 'Dashboard', to: '/admin' },
  { label: 'Alerts', to: '/admin/alerts' },
  { label: 'Artists', to: '/admin/artists' },
  { label: 'Collaborators', to: '/admin/collaborators' },
  { label: 'Broadcasts', to: '/admin/broadcasts' },
  { label: 'Artist Leads', to: '/admin/artist-leads' },
  { label: 'AI Insights', to: '/admin/ai-insights' },
  { label: 'AI Studio', to: '/admin/ai-studio' },
  { label: 'AI Mentor Pro', to: '/admin/ai-mentor' },
  { label: '🤖 KalaOps AI', to: '/admin/kalaops/overview' },
  { label: 'Trust Queue', to: '/admin/trust' },
  { label: 'Living EPK', to: '/admin/living-epk' },
  { label: 'Art Exhibition', to: '/admin/art-exhibition' },
  {
    label: 'Learn',
    children: [
      { label: 'Curricula & Plans', to: '/admin/learn' },
      { label: 'Cost Dashboard', to: '/admin/learn/costs' },
    ],
  },
  {
    label: 'Marketplace',
    children: [
      { label: 'Products', to: '/admin/marketplace/products' },
      { label: 'Orders', to: '/admin/marketplace/orders' },
    ],
  },
  { label: 'Project Approvals', to: '/admin/projects' },
  { label: 'Enquiries', to: '/admin/enquiries' },
  { label: 'Support Messages', to: '/admin/support' },
  { label: 'Support Content', to: '/admin/support-content' },
  { label: 'Content & SEO', to: '/admin/content-seo' },
  { label: 'Videos', to: '/admin/videos' },
  { label: 'Sponsors', to: '/admin/sponsors' },
  { label: 'Success Stories', to: '/admin/success-stories' },
  { label: 'Competitions', to: '/admin/competitions' },
  { label: 'Live Auditions', to: '/admin/live-auditions' },
  { label: 'Settings', to: '/admin/settings' },
];

const AdminLayout = () => {
  const [admin, setAdmin] = useState(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [alertPopup, setAlertPopup] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const loadAdmin = async () => {
      try {
        const response = await adminApi.get('/admin/me');
        if (mounted) {
          setAdmin(response.data);
        }
      } catch (error) {
        if (mounted) {
          setAdmin(null);
        }
      }
    };

    loadAdmin();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    let previousSnapshot = null;

    const loadAlertSummary = async () => {
      try {
        const response = await adminApi.get('/admin/notifications/summary');
        const items = response.data?.items || [];
        const snapshot = items.reduce((map, item) => {
          map[item.key] = `${item.count || 0}:${item.latest_id || ''}:${item.latest_at || ''}`;
          return map;
        }, {});

        if (previousSnapshot) {
          const changedItem = items.find((item) => previousSnapshot[item.key] && previousSnapshot[item.key] !== snapshot[item.key]);
          if (changedItem && mounted) {
            setAlertPopup({
              label: changedItem.label,
              title: changedItem.latest_title || `${changedItem.label} updated`,
              path: changedItem.view_path || changedItem.path || '/admin/alerts',
            });
          }
        }
        previousSnapshot = snapshot;
      } catch (error) {
        // Admin may be logged out; avoid interrupting the panel with polling errors.
      }
    };

    loadAlertSummary();
    const intervalId = window.setInterval(loadAlertSummary, 30000);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await adminApi.post('/admin/logout');
    } finally {
      navigate('/admin/login', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50">
      <div className="lg:flex">
        <aside className="hidden lg:flex lg:w-72 lg:flex-col lg:fixed lg:inset-y-0 bg-white border-r border-orange-100 shadow-sm">
          <div className="px-8 py-8 border-b border-orange-100">
            <Link to="/admin" className="inline-block">
              <div className="text-3xl font-bold gradient-text">Kala Manch</div>
            </Link>
            <p className="text-sm text-gray-500 mt-2">Admin control center</p>
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-1.5">
            {navItems.map((item) => (
              item.children ? (
                <div key={item.label} className="rounded-2xl border border-orange-100 bg-orange-50/60 p-2">
                  <p className="px-2 pb-1 text-xs font-black uppercase tracking-[0.18em] text-[#FF6F00]">{item.label}</p>
                  <div className="space-y-1">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        className={({ isActive }) =>
                          `block rounded-xl px-3 py-2 text-sm font-bold transition-all ${
                            isActive
                              ? 'bg-gradient-to-r from-[#FF6F00] to-[#4A148C] text-white shadow-lg'
                              : 'text-gray-700 hover:bg-white'
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/admin'}
                  className={({ isActive }) =>
                    `block rounded-2xl px-4 py-2.5 font-medium transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-[#FF6F00] to-[#4A148C] text-white shadow-lg'
                        : 'text-gray-700 hover:bg-orange-50'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            ))}
          </nav>

          <div className="shrink-0 px-5 py-4 border-t border-orange-100">
            <div className="rounded-2xl bg-orange-50 p-3 mb-3">
              <p className="text-sm font-semibold text-gray-800">{admin?.name || 'Admin'}</p>
              <p className="text-xs text-gray-500 mt-1">{admin?.email || 'Loading...'}</p>
            </div>
            <button className="btn-outline w-full" onClick={handleLogout}>
              Log Out
            </button>
          </div>
        </aside>

        <div className="flex-1 lg:ml-72">
          <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-orange-100">
            <div className="px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  className="btn-outline min-w-[5rem] shrink-0 whitespace-nowrap px-4 py-2 lg:hidden"
                  onClick={() => setIsMobileNavOpen((open) => !open)}
                >
                  Menu
                </button>
                <div>
                  <p className="text-sm text-gray-500">Admin panel</p>
                  <h1 className="text-xl font-bold text-gray-900">Kala Manch</h1>
                </div>
              </div>

              <div className="text-right">
                <p className="text-sm font-semibold text-gray-800">{admin?.name || 'Admin'}</p>
                <p className="text-xs text-gray-500">{admin?.email || 'Loading...'}</p>
              </div>
            </div>

            {isMobileNavOpen && (
              <div className="lg:hidden px-4 pb-4">
                <div className="rounded-3xl bg-white shadow-lg border border-orange-100 p-3 space-y-2">
                  {navItems.map((item) => (
                    item.children ? (
                      <div key={item.label} className="rounded-2xl border border-orange-100 bg-orange-50/60 p-2">
                        <p className="px-2 pb-1 text-xs font-black uppercase tracking-[0.18em] text-[#FF6F00]">{item.label}</p>
                        {item.children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            onClick={() => setIsMobileNavOpen(false)}
                            className={({ isActive }) =>
                              `block rounded-xl px-3 py-2 text-sm font-bold transition-all ${
                                isActive
                                  ? 'bg-gradient-to-r from-[#FF6F00] to-[#4A148C] text-white'
                                  : 'text-gray-700 hover:bg-white'
                              }`
                            }
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    ) : (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === '/admin'}
                        onClick={() => setIsMobileNavOpen(false)}
                        className={({ isActive }) =>
                          `block rounded-2xl px-4 py-3 font-medium transition-all ${
                            isActive
                              ? 'bg-gradient-to-r from-[#FF6F00] to-[#4A148C] text-white'
                              : 'text-gray-700 hover:bg-orange-50'
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    )
                  ))}
                  <button className="btn-outline w-full" onClick={handleLogout}>
                    Log Out
                  </button>
                </div>
              </div>
            )}
          </header>

          <main className="px-4 sm:px-6 lg:px-10 py-8">
            <Outlet context={{ admin }} />
          </main>
        </div>
      </div>
      {alertPopup && (
        <div className="fixed bottom-6 right-6 z-50 w-[min(92vw,360px)] rounded-3xl border border-orange-200 bg-white p-5 shadow-2xl">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Admin alert</p>
          <h3 className="mt-2 text-lg font-black text-gray-950">{alertPopup.label}</h3>
          <p className="mt-1 text-sm font-semibold text-gray-700">{alertPopup.title}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={alertPopup.path} onClick={() => setAlertPopup(null)} className="rounded-full bg-[#FF6F00] px-4 py-2 text-sm font-bold text-white">
              Open
            </Link>
            <button type="button" onClick={() => setAlertPopup(null)} className="rounded-full border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700">
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
